import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { FileText, Shield, ArrowLeft, CheckCircle, AlertTriangle, Ban, Info, RefreshCw } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  normal: { bg: '', border: '', textColor: 'text-slate-600', icon: null },
  warning: { bg: 'bg-amber-50', border: 'border border-amber-200 rounded-xl p-6', textColor: 'text-amber-800', icon: AlertTriangle },
  success: { bg: 'bg-green-50', border: 'border border-green-200 rounded-xl p-6', textColor: 'text-green-700', icon: Shield },
  danger: { bg: 'bg-red-50', border: 'border border-red-200 rounded-xl p-6', textColor: 'text-red-700', icon: Ban },
  info: { bg: 'bg-emerald-50', border: 'border border-emerald-200 rounded-xl p-6', textColor: 'text-emerald-700', icon: Info }
};

const TermsOfService = () => {
  const { language, localize } = useLanguage();
  const t = (ar, en) => localize({ ar, en });
  const [sections, setSections] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const res = await axios.get(`${API}/terms`);
      if (res.data.sections && res.data.sections.length > 0) {
        setSections(res.data.sections);
        setLastUpdated(res.data.last_updated);
      }
    } catch {
      // fallback: empty means show nothing extra
    } finally {
      setLoading(false);
    }
  };

  const renderContent = (text) => {
    if (!text) return null;
    return text.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={i} />;
      if (trimmed.startsWith('•')) {
        return (
          <li key={i} className="flex items-start gap-2 mb-1">
            <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5 opacity-60" />
            <span>{trimmed.substring(1).trim()}</span>
          </li>
        );
      }
      return <p key={i} className="mb-2">{trimmed}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-6" data-testid="back-link">
          <ArrowLeft className="w-4 h-4" />
          {t('العودة للرئيسية', 'Back to Home')}
        </Link>

        <Card className="border-0 shadow-lg">
          <CardHeader className="text-center border-b pb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
              <FileText className="w-8 h-8 text-emerald-600" />
            </div>
            <CardTitle className="text-3xl font-bold text-slate-900">
              {t('شروط الخدمة', 'Terms of Service')}
            </CardTitle>
            <p className="text-slate-500 mt-2">
              {lastUpdated
                ? `${t('آخر تحديث:', 'Last updated:')} ${new Date(lastUpdated).toLocaleDateString(language === 'ar' ? 'ar-AE' : 'en-US', { year: 'numeric', month: 'long' })}`
                : t('آخر تحديث: مارس 2026', 'Last updated: March 2026')
              }
            </p>
          </CardHeader>

          <CardContent className="p-8 space-y-8" data-testid="terms-content">
            {sections.length > 0 ? (
              sections.map((section, index) => {
                const style = TYPE_STYLES[section.type] || TYPE_STYLES.normal;
                const title = localize({ ar: section.title_ar, en: section.title_en });
                const content = localize({ ar: section.content_ar, en: section.content_en });
                const SectionIcon = style.icon;

                return (
                  <section key={section.id || index} data-testid={`terms-section-${index}`}>
                    <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                      {SectionIcon && <SectionIcon className="w-5 h-5" />}
                      {index + 1}. {title}
                    </h2>
                    {style.border ? (
                      <div className={`${style.bg} ${style.border}`}>
                        <div className={`${style.textColor} leading-relaxed`}>
                          {renderContent(content)}
                        </div>
                      </div>
                    ) : (
                      <div className={`${style.textColor} leading-relaxed`}>
                        {renderContent(content)}
                      </div>
                    )}
                  </section>
                );
              })
            ) : (
              /* Fallback static content */
              <>
                <section>
                  <h2 className="text-xl font-bold text-slate-900 mb-4">1. {t('مقدمة', 'Introduction')}</h2>
                  <p className="text-slate-600 leading-relaxed">
                    {t(
                      'مرحبًا بك في منصة "تعلم مع ثقة". باستخدامك لهذه المنصة، فإنك توافق على الالتزام بشروط الخدمة التالية.',
                      'Welcome to Learn With Trust. By using this platform, you agree to the following terms of service.'
                    )}
                  </p>
                </section>

                <section className="border-t pt-6">
                  <p className="text-center text-slate-500 text-sm">
                    {t('باستخدامك لهذه المنصة، فإنك توافق على جميع الشروط المذكورة أعلاه.', 'By using this platform, you agree to all the terms mentioned above.')}
                  </p>
                </section>
              </>
            )}

            {/* Footer agreement */}
            {sections.length > 0 && (
              <section className="border-t pt-6">
                <p className="text-center text-slate-500 text-sm">
                  {t('باستخدامك لهذه المنصة، فإنك توافق على جميع الشروط المذكورة أعلاه.', 'By using this platform, you agree to all the terms mentioned above.')}
                </p>
              </section>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
